/**
 * Helpers for merging V3 and V4 ARCs into a single list.
 *
 * The V4 platform exposes its own ARC API via lib-ui-core (`arcsList` and
 * friends). Some ARCs will live only in V4, so the admin UI needs to render
 * both sources together. We keep the V3 Immutable `ARC` model as the single
 * shape the rest of the app consumes — V4 rows are mapped down into it via
 * `mapV4ArcToV3` — and tag each record with a `source` field so views can
 * branch behaviour (save path, edit dialog sections, etc.) when needed.
 */

import { List } from 'immutable';
import ARC from '../../utils/models/ARC';
import V4API, { isV4TokenOnlySession } from '../../api/V4client';
import { AdminAPI } from '../../utils/API/AdminAPI';
import { mapV4ArcToV3 } from './transforms';

const adminAPI = new AdminAPI();

/**
 * Fetch V4 ARCs and map them to the V3 ARC model.
 * Returns an empty list on failure — the V3 list must still render.
 */
async function fetchV4ARCs() {
    try {
        const res = await V4API.arcsList();
        const items = res?.data || [];
        return items.map(a => new ARC(mapV4ArcToV3(a)));
    } catch (e) {
        // Don't block the V3 list when V4 is unreachable / not deployed.
        // eslint-disable-next-line no-console
        console.error('Failed to fetch V4 ARCs', e?.response?.data || e);
        return [];
    }
}

/**
 * Fetch V3 ARCs (legacy admin API). Returns an empty list on failure.
 */
async function fetchV3ARCs() {
    // A V4-token-only session can't authenticate against V3 — skip the request
    // rather than logging a guaranteed 401 on every ARC refetch.
    if (isV4TokenOnlySession()) return List();
    try {
        return await adminAPI.getARCs();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch V3 ARCs', e?.response?.data || e);
        return List();
    }
}

/**
 * Fetch V3 and V4 ARCs in parallel and return a single Immutable List.
 * V3 entries come first (preserving existing ordering), V4 entries append.
 */
export async function getMergedARCs() {
    const [v3, v4] = await Promise.all([fetchV3ARCs(), fetchV4ARCs()]);
    const v3List = List.isList(v3) ? v3 : List(v3 || []);
    return v3List.concat(List(v4));
}

// String-safe: callers pass a numeric V3 ARC `id` too.
export const normArcKey = k => String(k ?? '').trim().toLowerCase();

/**
 * An ARC's HUMAN key — the only form the JWT `arcs` claim holds. A V4 record's own
 * `.key` is its UUID (see `mapV4ArcToV3`), so read it off `v4Data`; a
 * `dedupeArcsByKey` twin already carries the V3 key.
 */
export function arcHumanKey(arc) {
    if (!arc) return '';
    if (arc.isV4 && !arc.hasV3Twin) return arc.v4Data?.key || '';
    return arc.key || '';
}

/** Does an `arcs` claim contain `key`? Case-insensitive — V4 keys are free-form. */
export function arcClaimMatches(claim, key) {
    const k = normArcKey(key);
    return !!k && (claim || []).some(c => normArcKey(c) === k);
}

/**
 * Collapse a V3 ARC and its V4 twin (same human key) into one display record.
 * Match is by human key: `v3.key` vs `v4.v4Data.key` (a V4 record's own `.key`
 * is the UUID). The V4 record wins for display; only `key` (the JWT claim and the
 * V3 `setARC` write speak human keys, not UUIDs) and `proms` (V4 has none) come
 * from V3. Tagged `hasV3Twin`. Dialog-local: `getMergedARCs` and other `arcs`
 * consumers stay un-deduped.
 */
export function dedupeArcsByKey(arcs) {
    const list = List.isList(arcs) ? arcs : List(arcs || []);
    const v4ByKey = new Map();
    list.forEach(a => {
        if (!a?.isV4) return;
        const hk = normArcKey(a.v4Data?.key);
        if (hk && !v4ByKey.has(hk)) v4ByKey.set(hk, a);
    });
    const mergedKeys = new Set();
    const out = [];
    list.forEach(a => {
        if (!a?.isV3) return;
        const hk = normArcKey(a.key);
        const twin = hk ? v4ByKey.get(hk) : null;
        if (twin) {
            mergedKeys.add(hk);
            out.push(twin.set('key', a.key).set('proms', a.proms).set('hasV3Twin', true));
        } else {
            out.push(a);
        }
    });
    list.forEach(a => {
        if (a?.isV4 && !mergedKeys.has(normArcKey(a.v4Data?.key))) out.push(a);
    });
    return List(out);
}

/**
 * Build the request body accepted by `arcsCreate` / `arcsUpdate`.
 * The V3 `ARC` Immutable model is our single UI shape; this helper
 * projects it down to the V4 wire format.
 */
function toV4ArcRequest(arc) {
    return {
        name: arc.name || '',
        // V4 requires a key (2–50 chars). On create the admin enters it (held on
        // arc.key); on update reuse the server's existing key — arc.key holds the
        // UUID for mapped V4 ARCs, so read the real key from v4Data.
        key: ((arc.id ? arc.v4Data?.key : arc.key) || '').trim(),
        label: arc.label || '',
        label_color: arc.color || '',
        trusted_numbers: arc.whitelistNumbers || [],
        // Emergency numbers round-trip like trusted_numbers (full-body PUT).
        emergency_numbers: arc.emergencyNumbers || [],
        // Exclusive closing: when true only the ARC may close its alarms.
        exclusive_closing: !!arc.exclusiveClosing,
        // Link requirements survive on v4Data when editing — use them
        // verbatim unless the UI has explicitly changed them.
        account_link_requirements: arc.v4Data?.account_link_requirements || [],
        device_link_requirements: arc.v4Data?.device_link_requirements || [],
    };
}

/**
 * Create or update a V4 ARC from our V3-shaped ARC record.
 * Returns the fresh V4 ARC payload from the server.
 */
export async function saveV4ARC(arc) {
    const body = toV4ArcRequest(arc);
    if (arc.id) {
        const res = await V4API.arcsUpdate(arc.id, body);
        return res?.data;
    }
    const res = await V4API.arcsCreate(body);
    return res?.data;
}

/**
 * Guard: a V4 link (account or device) may only ever target a V4 ARC. A V3-only
 * alarm center is not linkable — selecting one for a V4 device used to auto-create a
 * same-key V4 twin here, minting a duplicate ARC in V4 for every V3 alarm center a V4
 * device was pointed at. A dedup-merged row (`hasV3Twin`) IS the V4 record, so it
 * passes. The picker no longer offers V3-only rows to the V4 mechanism (`arcMechanism`
 * in ManageARCDialog); this is the write-side backstop.
 */
export function assertV4LinkTarget(arc) {
    if (!arc?.isV4) throw new Error('V4 links require a V4 ARC — a V3 alarm center is not linkable');
}

/**
 * Replace the full endpoints set for a V4 ARC by diffing against the
 * server's previous list (available on `arc.v4Data.endpoints`). This lets
 * the UI edit endpoints as a plain local array and have CRUD reconciled
 * in one call on save.
 *
 * @param {string} arcId
 * @param {{id?: string, url: string}[]} nextEndpoints
 * @param {{id: string, url: string}[]} prevEndpoints
 */
export async function syncV4Endpoints(arcId, nextEndpoints, prevEndpoints = []) {
    const prevById = new Map(prevEndpoints.map(e => [e.id, e]));
    const nextIds = new Set(nextEndpoints.filter(e => e.id).map(e => e.id));

    // Delete endpoints that were removed in the UI.
    const toDelete = prevEndpoints.filter(e => !nextIds.has(e.id));
    // Create endpoints that have no id yet.
    const toCreate = nextEndpoints.filter(e => !e.id && e.url);
    // Update endpoints whose URL changed.
    const toUpdate = nextEndpoints.filter(e => e.id && prevById.get(e.id)?.url !== e.url);

    await Promise.all([
        ...toDelete.map(e => V4API.arcsEndpointsDelete(arcId, e.id)),
        ...toCreate.map(e => V4API.arcsEndpointsCreate(arcId, { url: e.url })),
        ...toUpdate.map(e => V4API.arcsEndpointsUpdate(arcId, e.id, { url: e.url })),
    ]);
}

/**
 * Find the existing V4 device-link for a given device inside the ARC's
 * cached `v4Data.device_links` payload. Returns `null` when no link exists.
 * Comparing against `v4Data` avoids an extra round-trip — the link payload
 * was already fetched by `arcsList` and is kept on the ARC model.
 */
export function findExistingV4DeviceLink(arc, deviceId) {
    const links = arc?.v4Data?.device_links || [];
    return links.find(l => l.device_id === deviceId) || null;
}

// V4 links each resource (account or device) to at most one ARC and rejects a CREATE
// for one already linked — to this ARC (stale read) or another. Re-linking is never an
// implicit client-side move: the UI requires an explicit Unlink, so saves stay single
// writes.
function isAlreadyLinkedError(e) {
    const errors = e?.response?.data?.validation_errors;
    if (Array.isArray(errors) && errors.some(x => String(x).includes('already_linked'))) return true;
    // Some rejections carry the reason in `message` instead of validation_errors.
    const msg = String(e?.response?.data?.message || '').toLowerCase().replace(/_/g, ' ');
    return msg.includes('already linked');
}

// Deleting a link that's already gone reaches the desired state, so a stale list
// can't turn Unlink into an error the user has to retry.
const isNotFoundError = e => e?.response?.status === 404;

// eslint-disable-next-line no-console
const linkWarn = (...args) => console.warn('[V4 ARC link]', ...args);

/**
 * Resolve a resource's link record(s) on this ARC, cheapest source first: the ARC's
 * own `v4Data.*_links` (what the UI rendered from, zero requests), then the scoped
 * list, then the session-scoped one — either view can be blind to the other's links.
 *
 * Order is load-bearing: a scoped list can return an EMPTY array for a link the embed
 * shows, and an empty array is truthy, so `read() || embed` would keep the empty one —
 * resolving no link id, so Unlink issued no DELETE and no error (the original no-op).
 */
async function resolveLinks(embed, matches, reads = []) {
    const fromEmbed = (embed || []).filter(matches);
    if (fromEmbed.length) return { links: fromEmbed, source: 'embed' };
    for (let i = 0; i < reads.length; i++) {
        try {
            const found = ((await reads[i]()) || []).filter(matches);
            if (found.length) return { links: found, source: `read#${i}` };
        } catch (e) {
            linkWarn('link read failed', e?.response?.status, e?.response?.data || e);
        }
    }
    return { links: [], source: 'none' };
}

/**
 * DELETE one link id, trying each variant (scoped, then session-scoped) since the
 * caller can't know which scope the record lives in. Returns 'deleted', or 'notfound'
 * when every variant 404s; throws on a real error rather than pretending it worked.
 * A 404 must not short-circuit the rest — it only means "not deletable at this scope".
 */
async function tryDeleteLink(variants, ctx) {
    let lastErr = null;
    for (const attempt of variants) {
        try {
            await attempt();
            return 'deleted';
        } catch (e) {
            if (isNotFoundError(e)) continue;
            lastErr = e;
            linkWarn('unlink attempt failed', { ...ctx, status: e?.response?.status }, e?.response?.data || e);
        }
    }
    if (lastErr) throw lastErr;
    return 'notfound';
}

async function deleteEachLink(links, variantsFor, ctx) {
    let deleted = false;
    for (const link of links) {
        const outcome = await tryDeleteLink(variantsFor(link.id), { ...ctx, linkId: link.id });
        if (outcome === 'deleted') deleted = true;
    }
    return deleted;
}

/**
 * Unlink a resource from one ARC: resolve the link id (embed first), delete it across
 * scopes, and only report success once something was deleted or the server confirms
 * nothing is there. The second pass matters — a stale embed id 404s even when a live
 * link exists under another id, which would read as "already unlinked".
 */
async function unlinkFromArc({ embed, matches, reads, variantsFor, ctx }) {
    const first = await resolveLinks(embed, matches, reads);
    if (!first.links.length) {
        linkWarn('unlink found no link to delete', ctx);
        return;
    }
    if (await deleteEachLink(first.links, variantsFor, ctx)) return;
    // Every candidate 404'd. Server-sourced ids mean the absence is real; embed
    // ids can be stale, so confirm against the server before believing it.
    if (first.source !== 'embed') return;
    const fresh = await resolveLinks(null, matches, reads);
    if (!fresh.links.length) return;
    // Server ids that 404 on delete: its read model trails its writes, so the
    // link is gone. (tryDeleteLink rethrows anything that isn't a 404.)
    await deleteEachLink(fresh.links, variantsFor, ctx);
}

/**
 * Create, update, or delete a V4 device-link depending on the current state.
 *
 * - No existing link + non-empty providedData → CREATE
 * - Existing link + changed providedData     → UPDATE
 * - Existing link + unchanged                → no-op
 */
export async function saveV4DeviceLink(arc, device, providedData, params) {
    if (!arc?.id || !device?.id) return;
    // PREFER-V3-DEDUP: the shown row may be the V3 row enriched with the V4 uuid
    // (dedup overlay); V4 device-links are keyed by the V4 uuid, not the numeric
    // V3 id. To revert, use `device.id` directly for the lookup + create payload.
    const deviceUuid = device.uuid || device.id;
    // Same blind-cache problem as the account links — read them scoped first.
    const links = await readLinksOrCache(
        () => fetchV4DeviceLinks(arc.id, params),
        arc?.v4Data?.device_links || []
    );
    const existing = links.find(l => l.device_id === deviceUuid) || null;
    if (existing) {
        const prevData = existing.provided_data || {};
        const changed = JSON.stringify(prevData) !== JSON.stringify(providedData);
        if (!changed) return;
        await V4API.arcsDeviceLinksUpdate(arc.id, existing.id, { provided_data: providedData }, params);
        return;
    }
    try {
        await V4API.arcsDeviceLinksCreate(arc.id, { device_id: deviceUuid, provided_data: providedData }, params);
    } catch (e) {
        if (!isAlreadyLinkedError(e)) throw e;
        // Either the pre-write read was stale and the link is already HERE (so this is
        // really an edit), or the device is linked elsewhere and needs an explicit
        // Unlink — then surface the rejection. Server reads only: a stale embed would
        // turn a rejected CREATE into a reported success.
        const { links: found } = await resolveLinks(null, l => l.device_id === deviceUuid, [
            () => fetchV4DeviceLinks(arc.id, params),
            () => V4API.arcsDeviceLinksList(arc.id).then(r => r?.data || []),
        ]);
        const here = found[0];
        if (!here) throw e;
        if (JSON.stringify(here.provided_data || {}) !== JSON.stringify(providedData)) {
            await V4API.arcsDeviceLinksUpdate(arc.id, here.id, { provided_data: providedData }, params);
        }
    }
}

/**
 * Remove a V4 device-link by ARC + device. No-op when the device is not linked.
 */
export async function deleteV4DeviceLink(arc, device, params) {
    // PREFER-V3-DEDUP: key off the V4 uuid (was: device?.id).
    const deviceUuid = device?.uuid || device?.id;
    if (!arc?.id || !deviceUuid) {
        linkWarn('device unlink skipped — missing ids', { arcId: arc?.id, deviceUuid });
        return;
    }
    await unlinkFromArc({
        embed: arc?.v4Data?.device_links,
        matches: l => l.device_id === deviceUuid,
        reads: [
            () => fetchV4DeviceLinks(arc.id, params),
            () => V4API.arcsDeviceLinksList(arc.id).then(r => r?.data || []),
        ],
        // Without `params` the session-scoped retry would be the identical request.
        variantsFor: id => [
            () => V4API.arcsDeviceLinksDelete(arc.id, id, params),
            ...(params ? [() => V4API.arcsDeviceLinksDelete(arc.id, id)] : []),
        ],
        ctx: { kind: 'device', arcId: arc.id, deviceUuid },
    });
}

/**
 * Find the existing V4 account-link for a given V4 account inside the ARC's
 * cached `v4Data.account_links`. Returns `null` if the account isn't linked.
 */
export function findExistingV4AccountLink(arc, accountId) {
    const links = arc?.v4Data?.account_links || [];
    return links.find(l => l.account_id === accountId) || null;
}

/**
 * The V4 account-link endpoints don't accept `account_id` in the body — the
 * platform infers it from the `X-Account-Scope` header. This helper wraps
 * every call with that header so callers just pass the V4 account UUID.
 */
const scopedHeaders = (accountId) => ({ headers: { 'X-Account-Scope': accountId } });

/**
 * Create or update a V4 account-link for the given ARC + V4 account.
 *
 * - No existing link                             → CREATE
 * - Existing link + changed providedData         → UPDATE
 * - Existing link + unchanged providedData       → no-op
 */
export async function fetchV4AccountLinks(arcId, accountId) {
    if (!arcId || !accountId) return null;
    const res = await V4API.arcsAccountLinksList(arcId, scopedHeaders(accountId));
    return res?.data || [];
}

export async function fetchV4DeviceLinks(arcId, params) {
    if (!arcId) return null;
    const res = await V4API.arcsDeviceLinksList(arcId, params);
    return res?.data || [];
}

// The pre-write read may legitimately degrade to the cached list; a failed
// post-write read must reject so the caller reports it.
async function readLinksOrCache(read, cached) {
    try {
        return (await read()) || cached;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to read V4 links', e?.response?.data || e);
        return cached;
    }
}

export async function saveV4AccountLink(arc, accountId, providedData) {
    if (!arc?.id || !accountId) return null;
    // arcsList runs unscoped, so a manager's cached ARC hides a sub-account's
    // links — read them under the account's own scope before choosing.
    const links = await readLinksOrCache(
        () => fetchV4AccountLinks(arc.id, accountId),
        arc?.v4Data?.account_links || []
    );
    const existing = links.find(l => l.account_id === accountId) || null;
    if (existing) {
        const prevData = existing.provided_data || {};
        if (JSON.stringify(prevData) !== JSON.stringify(providedData)) {
            await V4API.arcsAccountLinksUpdate(arc.id, existing.id, { provided_data: providedData }, scopedHeaders(accountId));
        }
    } else {
        try {
            await V4API.arcsAccountLinksCreate(arc.id, { provided_data: providedData }, scopedHeaders(accountId));
        } catch (e) {
            if (!isAlreadyLinkedError(e)) throw e;
            // Same recovery as the device saver above (server reads only).
            const { links: found } = await resolveLinks(null, l => l.account_id === accountId, [
                () => fetchV4AccountLinks(arc.id, accountId),
                () => V4API.arcsAccountLinksList(arc.id).then(r => r?.data || []),
            ]);
            const here = found[0];
            if (!here) throw e;
            if (JSON.stringify(here.provided_data || {}) !== JSON.stringify(providedData)) {
                await V4API.arcsAccountLinksUpdate(arc.id, here.id, { provided_data: providedData }, scopedHeaders(accountId));
            }
        }
    }
    // Hand the post-write state back so the caller can patch the cached ARC.
    return await fetchV4AccountLinks(arc.id, accountId);
}

/**
 * A device may only be linked to its account's ARC, so when the account leaves an ARC
 * its devices' links there go too — otherwise they keep forwarding alarms to an ARC
 * the account left. Devices are matched by the link's `owner_id`. Best-effort: the
 * request was to unlink the ACCOUNT, so a device that won't release is only logged.
 */
async function unlinkAccountDevicesFromArc(arc, accountId) {
    const { links } = await resolveLinks(
        arc?.v4Data?.device_links,
        l => l.owner_id === accountId,
        [
            () => fetchV4DeviceLinks(arc.id, scopedHeaders(accountId)),
            () => V4API.arcsDeviceLinksList(arc.id).then(r => r?.data || []),
        ]
    );
    for (const link of links) {
        try {
            await tryDeleteLink([
                () => V4API.arcsDeviceLinksDelete(arc.id, link.id, scopedHeaders(accountId)),
                () => V4API.arcsDeviceLinksDelete(arc.id, link.id),
            ], { kind: 'device-cascade', arcId: arc.id, linkId: link.id, deviceId: link.device_id });
        } catch (e) {
            linkWarn('cascade unlink failed for device', link.device_id, e?.response?.data || e);
        }
    }
}

/**
 * Remove a V4 account-link, then cascade the account's device-links off the same
 * ARC (see unlinkAccountDevicesFromArc). No-op when the account isn't linked.
 */
export async function deleteV4AccountLink(arc, accountId) {
    if (!arc?.id || !accountId) {
        linkWarn('account unlink skipped — missing ids', { arcId: arc?.id, accountId });
        return;
    }
    await unlinkFromArc({
        embed: arc?.v4Data?.account_links,
        matches: l => l.account_id === accountId,
        reads: [
            () => fetchV4AccountLinks(arc.id, accountId),
            () => V4API.arcsAccountLinksList(arc.id).then(r => r?.data || []),
        ],
        variantsFor: id => [
            () => V4API.arcsAccountLinksDelete(arc.id, id, scopedHeaders(accountId)),
            () => V4API.arcsAccountLinksDelete(arc.id, id),
        ],
        ctx: { kind: 'account', arcId: arc.id, accountId },
    });
    // After the account link, never before: the admin's request is the account
    // unlink, and it must not be jeopardised by a device that won't release.
    await unlinkAccountDevicesFromArc(arc, accountId);
}

/**
 * Fetch the list of V4 protocols. ARC endpoints reference protocols by name
 * (encoded as `<protocol>://<host>:<port>`), so the ARC details UI uses
 * this list to drive its protocol dropdown.
 */
export async function fetchV4Protocols() {
    try {
        const res = await V4API.protocolsList();
        return res?.data || [];
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch V4 protocols', e?.response?.data || e);
        return null;
    }
}

/**
 * Create or update a V4 protocol.
 *
 * - `originalName` is provided when editing an existing protocol so we can
 *   call `protocolsUpdate(originalName, ...)` even when the user has changed
 *   the displayed name. (Renames go through the same PUT endpoint.)
 * - When `originalName` is null we POST to create a brand-new protocol.
 */
export async function saveV4Protocol(protocol, originalName = null) {
    const body = {
        name: protocol.name,
        alarm_template: protocol.alarm_template || '',
        poll_template: protocol.poll_template || '',
    };
    if (originalName) {
        const res = await V4API.protocolsUpdate(originalName, body);
        return res?.data;
    }
    const res = await V4API.protocolsCreate(body);
    return res?.data;
}

/**
 * Delete a V4 protocol by name.
 */
export async function deleteV4Protocol(name) {
    if (!name) return;
    await V4API.protocolsDelete(name);
}
